/**
 * Fixed-aspect-ratio letterboxing.
 *
 * Rather than reflowing the panel/scene layout for every possible window
 * shape (fragile — small aspect-ratio-dependent math errors kept resurfacing
 * as real clipping bugs), everything is rendered at one canonical 16:9 (UHD)
 * virtual resolution and that rectangle is scaled as large as fits inside the
 * actual browser window, centred, with the remainder left as black bars
 * (pillarbox on wide windows, letterbox on tall ones). All panel/scene layout
 * math is written once against this fixed aspect ratio and never needs to
 * special-case a window shape again.
 */

export const ASPECT_W = 16;
export const ASPECT_H = 9;
export const ASPECT = ASPECT_W / ASPECT_H;

// Cap the rendered (backing-buffer) resolution at true UHD even if the fitted
// rectangle, scaled by devicePixelRatio, would exceed it (e.g. an 8K display).
export const MAX_W = 3840;
export const MAX_H = 2160;

export interface FitRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Largest 16:9 rectangle that fits within (windowW, windowH), centred. */
export function computeFitRect(windowW: number, windowH: number): FitRect {
  let width: number, height: number;
  if (windowW / windowH > ASPECT) {
    height = windowH;
    width = height * ASPECT;
  } else {
    width = windowW;
    height = width / ASPECT;
  }
  return { x: (windowW - width) / 2, y: (windowH - height) / 2, width, height };
}

/** Position/size an element (e.g. a canvas) to exactly cover a FitRect. */
export function applyFitRect(el: HTMLElement, rect: FitRect): void {
  el.style.position = 'absolute';
  el.style.left = `${rect.x}px`;
  el.style.top = `${rect.y}px`;
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
}

/** Backing-buffer pixel size for a canvas at this rect: DPR-scaled, capped at UHD. */
export function backingResolution(rect: FitRect): { w: number; h: number } {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = Math.min(Math.round(rect.width * dpr), MAX_W);
  const h = Math.min(Math.round(rect.height * dpr), MAX_H);
  return { w, h };
}
